import { Typography, useTheme } from '@material-ui/core';
import CropFreeIcon from '@material-ui/icons/CropFree';
import React, { useState } from 'react';
import { Button, useTranslate } from 'react-admin';

import type { StorageEntityInfo } from '../types/warehouse.types';
import { formatDateOnly } from '../utils/dateFormatters';
import { formatFloat } from '../utils/numberFormatters';
import { formatProductLabel } from '../utils/palletFormatters';
import CodeDialog from './CodeDialog';
import { InfoField } from './InfoField';
import { OrderLink } from './OrderLink';
import { valueTextStyle } from './styles';

interface SinglePalletInfoProps {
  storageEntity: StorageEntityInfo;
  showTitle?: boolean;
  showExtendedInfo?: boolean;
}

export const SinglePalletInfo: React.FC<SinglePalletInfoProps> = ({ storageEntity, showTitle = true, showExtendedInfo = true }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [openBarcode, setOpenBarcode] = useState(false);
  const dynamicTextStyle = valueTextStyle(theme);

  return (
    <div style={{ paddingTop: showTitle ? '15px' : '0' }}>
      {showTitle && (
        <Typography component="p" variant="subtitle2" style={{ margin: 0, fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
          {translate('osfc.storage_ui.pallet_info.title')}
        </Typography>
      )}
      <InfoField label={translate('osfc.storage_ui.pallet_info.customer')} value={storageEntity.customer_name?.toUpperCase()} valueStyle={dynamicTextStyle} />

      <InfoField label={translate('osfc.storage_ui.pallet_info.incoming_order')} value={storageEntity.order_ref ? <OrderLink orderId={storageEntity.order_id} orderRef={storageEntity.order_ref} /> : null} />
      {storageEntity.reserved_order_ref && (
        <InfoField label={translate('osfc.storage_ui.pallet_info.outgoing_order')} value={<OrderLink orderId={storageEntity.reserved_order_id} orderRef={storageEntity.reserved_order_ref} />} />
      )}

      <InfoField label={translate('osfc.storage_ui.pallet_info.sscc')} value={storageEntity.pallet_sscc} valueStyle={dynamicTextStyle} />
      <InfoField label={translate('osfc.storage_ui.pallet_info.ceel')} value={storageEntity.ceel_number} valueStyle={dynamicTextStyle} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <InfoField label={translate('osfc.storage_ui.pallet_info.boxes')} value={storageEntity.boxes} valueStyle={dynamicTextStyle} />
        <InfoField label={translate('osfc.storage_ui.pallet_info.kilos')} value={formatFloat(storageEntity.kilos)} valueStyle={dynamicTextStyle} />
      </div>
      <InfoField label={translate('osfc.storage_ui.pallet_info.product')} value={formatProductLabel(storageEntity.product_number, storageEntity.product_name)} valueStyle={dynamicTextStyle} />
      <InfoField label={translate('osfc.storage_ui.pallet_info.production')} value={formatDateOnly(storageEntity.production_date)} valueStyle={dynamicTextStyle} />
      <InfoField label={translate('osfc.storage_ui.pallet_info.expiration')} value={formatDateOnly(storageEntity.expiration_date)} valueStyle={dynamicTextStyle} />
      {showExtendedInfo && (
        <>
          <Button onClick={() => setOpenBarcode(true)} style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CropFreeIcon />
              <Typography variant="body2" component="span">
                {translate('osfc.storage_ui.pallet_info.show_barcode')}
              </Typography>
            </div>
          </Button>

          <CodeDialog
            open={openBarcode}
            type='barcode'
            onClose={() => setOpenBarcode(false)}
            value={storageEntity.pallet_sscc}
            title={translate('osfc.storage_ui.pallet_info.pallet_id', { id: storageEntity.id })}
          />
        </>
      )}
    </div>
  );
};
